import { calculateViewportsSpatialRegistration } from "@cornerstonejs/core/utilities";
import {
  getAllViewports
} from "./CornerstoneViewportService";
import { getEnabledElement } from "@cornerstonejs/core";
import { SynchronizerManager } from "@cornerstonejs/tools";
import { createStackImageSynchronizer } from "@cornerstonejs/tools/synchronizers";
import { SYNC_MODES } from "../../../constants/Constants";

let STACK_IMAGE_SYNC_GROUPS_INFO = [];
let SYNC_ENABLE_REQUEST_ID = 0;

const clearSyncGroups = (invalidateInFlight = true) => {
  if (invalidateInFlight) {
    SYNC_ENABLE_REQUEST_ID++;
  }

  STACK_IMAGE_SYNC_GROUPS_INFO.forEach((syncGroupInfo) => {
    const { viewports, synchronizerId } = syncGroupInfo;

    viewports.forEach(({ viewportId, renderingEngineId }) => {
      removeViewportFromSyncGroup(
        viewportId,
        renderingEngineId,
        synchronizerId
      );
    });
  });

  STACK_IMAGE_SYNC_GROUPS_INFO = [];
};

const getOrCreateSynchronizer = (id) => {
  let synchronizer = SynchronizerManager.getSynchronizer(id);

  if (!synchronizer) {
    synchronizer = createStackImageSynchronizer(id);
  }

  return synchronizer;
};

/**
 * Enable scroll synchronization across viewports.
 * @param {number} viewportIndex - The active viewport index
 * @param {string} mode - SYNC_MODES value: 'disabled' | 'manual' | 'automatic' | 'distance'
 * @param {Array} viewportSeriesData - Array of viewport series data (used in 'automatic' mode to filter by study)
 * @param {Array} linkedViewportIndices - Optional array of viewport indices to include in sync (null = all)
 */
export const enableStackScrollSync = async (viewportIndex, mode = SYNC_MODES.MANUAL, viewportSeriesData = [], linkedViewportIndices = null) => {
  const requestId = ++SYNC_ENABLE_REQUEST_ID;

  // Disabled mode: tear down everything
  if (mode === SYNC_MODES.DISABLED) {
    await disableStackScrollSync();
    return;
  }

  // Always rebuild synchronizers from a clean slate to avoid stale/partial
  // groups when this function is called repeatedly from different UI flows.
  clearSyncGroups(false);

  if (requestId !== SYNC_ENABLE_REQUEST_ID) {
    return;
  }

  let viewports = await getAllViewports();

  viewports = viewports.filter(
    (viewport) =>
      viewport.getCurrentImageId() !== undefined &&
      viewport.type !== "orthographic" && !viewport.id.startsWith("thumbnail")
  );

  // Keep ordering deterministic so synchronizer IDs stay stable.
  viewports = viewports.sort((a, b) => a.id.localeCompare(b.id));

  const getViewportIndex = (id) => {
    const match = String(id).match(/(\d+)$/);
    return match ? Number(match[1]) : -1;
  };

  const getStackImageCount = (viewport) => {
    const vpIndex = getViewportIndex(viewport.id);
    const seriesStack = viewportSeriesData?.[vpIndex]?.stack;
    const fromSeriesData = seriesStack?.thumbnailImageIds?.length;
    if (typeof fromSeriesData === "number") {
      return fromSeriesData;
    }

    const { element } = viewport;
    const { viewport: csViewport } = getEnabledElement(element);
    const imageIds = csViewport?.getImageIds?.();
    return Array.isArray(imageIds) ? imageIds.length : 0;
  };

  // In automatic mode, only sync viewports belonging to the same study as the active viewport
  if (mode === SYNC_MODES.AUTOMATIC && viewportSeriesData.length > 0) {
    const activeStudy = viewportSeriesData[viewportIndex]?.study;
    if (activeStudy) {
      viewports = viewports.filter((viewport) => {
        const vpIndex = getViewportIndex(viewport.id);
        return viewportSeriesData[vpIndex]?.study === activeStudy;
      });
    }
  }

  // Filter by linked viewport indices if provided
  if (linkedViewportIndices && linkedViewportIndices.length > 0) {
    viewports = viewports.filter((viewport) => {
      const vpIndex = getViewportIndex(viewport.id);
      return linkedViewportIndices.includes(vpIndex);
    });
  }

  if (requestId !== SYNC_ENABLE_REQUEST_ID) {
    return;
  }

  // Stack-scroll synchronization should only include viewports that can actually
  // scroll (single-image series like topograms should not be in stack sync).
  let stackSyncCandidates = viewports.filter((viewport) => getStackImageCount(viewport) > 1);

  // In automatic mode, if active-study filtering leaves fewer than 2 scrollable
  // viewports, fallback to all scrollable candidates (respecting linked filter).
  if (mode === SYNC_MODES.AUTOMATIC && stackSyncCandidates.length < 2) {
    let fallbackViewports = await getAllViewports();
    fallbackViewports = fallbackViewports.filter(
      (viewport) =>
        viewport.getCurrentImageId() !== undefined &&
        viewport.type !== "orthographic" &&
        !viewport.id.startsWith("thumbnail")
    );
    fallbackViewports = fallbackViewports.sort((a, b) => a.id.localeCompare(b.id));

    if (linkedViewportIndices && linkedViewportIndices.length > 0) {
      fallbackViewports = fallbackViewports.filter((viewport) => {
        const vpIndex = getViewportIndex(viewport.id);
        return linkedViewportIndices.includes(vpIndex);
      });
    }

    stackSyncCandidates = fallbackViewports.filter((viewport) => getStackImageCount(viewport) > 1);
  }

  const synchronizedViewports = stackSyncCandidates
    .filter((viewport) => viewport.type === "stack")
    .map((viewport) => {
      const { id: viewportId, element } = viewport;
      const { viewport: csViewport, renderingEngineId } = getEnabledElement(element);
      return { viewportId, renderingEngineId, csViewport };
    });

  if (synchronizedViewports.length < 2) {
    return;
  }

  calculateViewportRegistration(synchronizedViewports);

  const synchronizerId = synchronizedViewports
    .map(({ viewportId }) => viewportId)
    .join(",");

  // All modes (Manual, Automatic, Distance) use image-slice sync + spatial registration.
  // Distance differs from Automatic only in not filtering by same study (handled above).
  const sliceSyncId = `imageSync_${synchronizerId}`;
  synchronizedViewports.forEach(({ viewportId, renderingEngineId }) => {
    addViewportToSyncGroup(viewportId, renderingEngineId, {
      id: sliceSyncId,
      source: true,
      target: true,
    });
  });
  STACK_IMAGE_SYNC_GROUPS_INFO.push({
    synchronizerId: sliceSyncId,
    viewports: synchronizedViewports,
  });

  // Zoom and pan are intentionally NOT synchronized across viewports: only
  // the image in the viewport being interacted with should zoom/pan.
};

export const disableStackScrollSync = async () => {
  clearSyncGroups(true);

  return;
};

const addViewportToSyncGroup = (viewportId, renderingEngineId, syncGroups) => {
  if (!syncGroups) {
    return;
  }

  const syncGroupsArray = Array.isArray(syncGroups) ? syncGroups : [syncGroups];

  syncGroupsArray.forEach((syncGroup) => {
    const { id, source, target } = syncGroup;
    const synchronizer = getOrCreateSynchronizer(id);

    const viewportInfo = { viewportId, renderingEngineId };
    if (target && source) {
      synchronizer.add(viewportInfo);
      return;
    } else if (source) {
      synchronizer.addSource(viewportInfo);
    } else if (target) {
      synchronizer.addTarget(viewportInfo);
    }
  });
};

const removeViewportFromSyncGroup = (
  viewportId,
  renderingEngineId,
  syncGroupId
) => {
  const synchronizers = SynchronizerManager.getAllSynchronizers();

  const filteredSynchronizers = syncGroupId
    ? synchronizers.filter((s) => s.id === syncGroupId)
    : synchronizers;

  filteredSynchronizers.forEach((synchronizer) => {
    if (!synchronizer) {
      return;
    }

    synchronizer.remove({
      viewportId,
      renderingEngineId,
    });

    // check if any viewport is left in any of the sync groups, if not, delete that sync group
    const sourceViewports = synchronizer.getSourceViewports();
    const targetViewports = synchronizer.getTargetViewports();

    if (!sourceViewports.length && !targetViewports.length) {
      SynchronizerManager.destroySynchronizer(synchronizer.id);
    }
  });
};

const calculateViewportRegistration = (viewports) => {
  const viewportPairs = getViewportPairs(viewports);
  for (const [viewport, nextViewport] of viewportPairs) {
    const csViewport1 = viewport.csViewport;
    const csViewport2 = nextViewport.csViewport;

    calculateViewportsSpatialRegistration(csViewport1, csViewport2);
  }
};

const getViewportPairs = (viewports) => {
  const viewportPairs = [];

  //   for (let j = 0; j < target.length; j++) {
  //     viewportPairs.push([source, target[j]]);
  //   }

  for (let i = 0; i < viewports.length; i++) {
    for (let j = i + 1; j < viewports.length; j++) {
      viewportPairs.push([viewports[i], viewports[j]]);
    }
  }

  return viewportPairs;
};
